<template>
	<div class="modules-list">
		<!-- Header -->
		<div class="modules-list__header">
			<h3 class="modules-list__title">Модули курса</h3>
			<button
				v-if="!loading"
				class="modules-list__add-btn"
				type="button"
				@click="$emit('create-module')"
			>
				<span class="modules-list__add-icon">+</span>
				Добавить модуль
			</button>
		</div>

		<!-- Loading -->
		<div v-if="loading" class="modules-list__loading">
			<div class="modules-list__skeleton">
				<div class="skeleton-card" v-for="n in 3" :key="n">
					<div class="skeleton-line skeleton-line--title"></div>
					<div class="skeleton-line"></div>
					<div class="skeleton-line skeleton-line--short"></div>
				</div>
			</div>
			<p>Загрузка модулей...</p>
		</div>

		<div v-else-if="loadError" class="modules-list__error">
			<p class="modules-list__error-text">{{ loadError }}</p>
			<button class="modules-list__retry-btn" type="button" @click="$emit('retry-load')">
				Повторить загрузку
			</button>
		</div>

		<!-- Empty State -->
		<div v-else-if="modules.length === 0" class="modules-list__empty">
			<p class="modules-list__empty-text">Модули еще не созданы</p>
			<button
				class="modules-list__empty-btn"
				type="button"
				@click="$emit('create-module')"
			>
				Создать первый модуль
			</button>
		</div>

		<!-- Modules List -->
		<div v-else class="modules-list__items modules-list__items--enter">
			<draggable
				v-model="localModules"
				@end="handleReorder"
				item-key="id"
				handle=".module-card__drag-handle"
			>
				<template #item="{ element: module }">
					<ModuleCard
						:module="module"
						@edit="$emit('edit-module', module)"
						@delete="$emit('delete-module', module)"
						@publish="$emit('publish-module', module)"
						@unpublish="$emit('unpublish-module', module)"
						@view-lessons="$emit('view-lessons', module)"
					/>
				</template>
			</draggable>
		</div>
	</div>
</template>

<script>
import ModuleCard from './ModuleCard.vue'
import draggable from 'vuedraggable'

export default {
	name: 'ModulesList',
	components: {
		ModuleCard,
		draggable,
	},
	props: {
		modules: {
			type: Array,
			default: () => [],
		},
		loading: {
			type: Boolean,
			default: false,
		},
		loadError: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			localModules: [...this.modules],
		}
	},
	watch: {
		modules: {
			handler(newModules) {
				this.localModules = [...newModules]
			},
			deep: true,
		},
	},
	methods: {
		handleReorder() {
			const moduleIds = this.localModules.map(m => m.id)
			this.$emit('reorder-modules', moduleIds)
		},
	},
}
</script>

<style scoped>
.modules-list {
	width: 100%;
	padding: 20px;
	background-color: #ffffff;
	border-radius: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Header */
.modules-list__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
}

.modules-list__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 24px;
	font-weight: 600;
	margin: 0;
}

.modules-list__add-btn {
	display: flex;
	align-items: center;
	gap: 8px;
	background-color: #0095ec;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	padding: 10px 20px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
}

.modules-list__add-btn:hover {
	background-color: #0077be;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}

.modules-list__add-icon {
	font-size: 20px;
	font-weight: 600;
}

/* Loading */
.modules-list__loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
}

.modules-list__skeleton {
	width: 100%;
	display: grid;
	gap: 12px;
	margin-bottom: 16px;
}

.skeleton-card {
	border-radius: 12px;
	padding: 16px;
	background: #f5f7fb;
	border: 1px solid #e8eef7;
	animation: modules-skeleton-rise 0.45s ease both;
}

.skeleton-card:nth-child(2) {
	animation-delay: 0.08s;
}

.skeleton-card:nth-child(3) {
	animation-delay: 0.16s;
}

.skeleton-line {
	height: 12px;
	border-radius: 8px;
	background: linear-gradient(90deg, #e9eef6 25%, #f6f9ff 50%, #e9eef6 75%);
	background-size: 220% 100%;
	animation: modules-skeleton-shimmer 1.1s infinite linear;
	margin-bottom: 10px;
}

.skeleton-line--title {
	height: 16px;
	width: 60%;
}

.skeleton-line--short {
	width: 40%;
	margin-bottom: 0;
}

@keyframes modules-skeleton-shimmer {
	0% {
		background-position: 100% 0;
	}
	100% {
		background-position: -100% 0;
	}
}

@keyframes modules-skeleton-rise {
	0% {
		opacity: 0;
		transform: translateY(10px);
	}
	100% {
		opacity: 1;
		transform: translateY(0);
	}
}

.modules-list__error {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	text-align: center;
}

.modules-list__error-text {
	color: #b42318;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	margin-bottom: 12px;
}

.modules-list__retry-btn {
	background-color: #0095ec;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	padding: 10px 18px;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
}

.modules-list__retry-btn:hover {
	background-color: #0077be;
}

/* Empty State */
.modules-list__empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	text-align: center;
}

.modules-list__empty-text {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	margin-bottom: 20px;
}

.modules-list__empty-btn {
	background-color: #0095ec;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	padding: 12px 24px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
}

.modules-list__empty-btn:hover {
	background-color: #0077be;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}

/* Items */
.modules-list__items {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.modules-list__items--enter :deep(.module-card) {
	opacity: 0;
	transform: translateY(10px) scale(0.995);
	animation: modules-card-enter 0.32s ease forwards;
}

.modules-list__items--enter :deep(.module-card:nth-child(1)) {
	animation-delay: 0.03s;
}

.modules-list__items--enter :deep(.module-card:nth-child(2)) {
	animation-delay: 0.07s;
}

.modules-list__items--enter :deep(.module-card:nth-child(3)) {
	animation-delay: 0.11s;
}

.modules-list__items--enter :deep(.module-card:nth-child(4)) {
	animation-delay: 0.15s;
}

.modules-list__items--enter :deep(.module-card:nth-child(n + 5)) {
	animation-delay: 0.19s;
}

@keyframes modules-card-enter {
	0% {
		opacity: 0;
		transform: translateY(10px) scale(0.995);
	}
	100% {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}
</style>